import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Cookie-based auth: httpOnly session cookie + readable CSRF cookie
// (double-submit pattern). axios sends cookies via withCredentials.
const client = axios.create({ baseURL: API, withCredentials: true });

function getCookie(name) {
  const m = document.cookie.match(new RegExp("(^|; )" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[2]) : null;
}

client.interceptors.request.use((config) => {
  // Attach CSRF token for state-changing requests
  const method = (config.method || "get").toLowerCase();
  if (["post", "put", "patch", "delete"].includes(method)) {
    const csrf = getCookie("cod_csrf");
    if (csrf) config.headers["X-CSRF-Token"] = csrf;
  }
  return config;
});

export default client;

export const authApi = {
  signup: (data) => client.post("/auth/signup", data).then(r => r.data),
  login: (data) => client.post("/auth/login", data).then(r => r.data),
  logout: () => client.post("/auth/logout").then(r => r.data),
  me: () => client.get("/auth/me").then(r => r.data),
};

export const navApi = {
  chat: (data) => client.post("/navigator/chat", data).then(r => r.data),
  conversations: () => client.get("/navigator/conversations").then(r => r.data),
  conversation: (id) => client.get(`/navigator/conversations/${id}`).then(r => r.data),
  generatePlan: (cid) => client.post("/navigator/plan", { conversation_id: cid }).then(r => r.data),
  publicChat: (session_id, message, history) => client.post("/navigator/public", { session_id, message, history }).then(r => r.data),
  contextTransfer: (session_id, messages, lead) => client.post("/navigator/context-transfer", { session_id, messages, lead }).then(r => r.data),
};

export const plansApi = {
  mine: () => client.get("/plans/me").then(r => r.data),
  active: () => client.get("/plans/me/active").then(r => r.data),
  addItem: (opportunity_slug) => client.post("/plans/me/add-item", { opportunity_slug }).then(r => r.data),
};

export const tasksApi = {
  mine: () => client.get("/tasks/me").then(r => r.data),
  create: (d) => client.post("/tasks", d).then(r => r.data),
  update: (id, d) => client.patch(`/tasks/${id}`, d).then(r => r.data),
};

export const docsApi = {
  mine: (category) => client.get("/documents/me", { params: category ? { category } : {} }).then(r => r.data),
  categories: () => client.get("/documents/categories").then(r => r.data),
  upload: (form) => client.post("/documents/upload", form, { headers: { "Content-Type":"multipart/form-data" }}).then(r => r.data),
  replace: (id, form) => client.post(`/documents/${id}/replace`, form, { headers: { "Content-Type":"multipart/form-data" }}).then(r => r.data),
  remove: (id) => client.delete(`/documents/${id}`).then(r => r.data),
  versions: (id) => client.get(`/documents/${id}/versions`).then(r => r.data),
  verify: (id, verified=true) => {
    const fd = new FormData();
    fd.append("verified", verified ? "true" : "false");
    return client.post(`/documents/${id}/verify`, fd, { headers: { "Content-Type":"multipart/form-data" }}).then(r => r.data);
  },
  participantDocs: (pid, category) => client.get(`/documents/participant/${pid}`, { params: category ? { category } : {} }).then(r => r.data),
  uploadForParticipant: (pid, form) => {
    form.append("owner_user_id", pid);
    return client.post("/documents/upload", form, { headers: { "Content-Type":"multipart/form-data" }}).then(r => r.data);
  },
  downloadUrl: (id) => `${API}/documents/${id}/download`,
  previewUrl: (id) => `${API}/documents/${id}/preview`,
  // Authenticated blob fetch (since <img>/<iframe> cannot send cookies cross-site reliably we still use credentials)
  fetchBlob: (id, inline=true) =>
    client.get(`/documents/${id}/${inline ? "preview" : "download"}`, { responseType: "blob" })
      .then(r => r.data),
};

export const apptApi = {
  mine: (includeHistory=false) => client.get("/appointments/me", { params: includeHistory ? { include_history: true } : {} }).then(r => r.data),
  create: (d) => client.post("/appointments", d).then(r => r.data),
  reschedule: (id, d) => client.post(`/appointments/${id}/reschedule`, d).then(r => r.data),
  cancel: (id, d) => client.post(`/appointments/${id}/cancel`, d).then(r => r.data),
  history: (id) => client.get(`/appointments/${id}/history`).then(r => r.data),
  participant: (pid, includeHistory=false) => client.get(`/appointments/participant/${pid}`, { params: includeHistory ? { include_history: true } : {} }).then(r => r.data),
  generateReminders: () => client.post("/appointments/generate-reminders").then(r => r.data),
};

export const msgApi = {
  mine: () => client.get("/messages/me").then(r => r.data),
  send: (d) => client.post("/messages", d).then(r => r.data),
};

export const notifApi = {
  mine: () => client.get("/notifications/me").then(r => r.data),
  read: (id) => client.post(`/notifications/${id}/read`).then(r => r.data),
};

export const marketApi = {
  opportunities: (params) => client.get("/marketplace/opportunities", { params }).then(r => r.data),
  opportunity: (id) => client.get(`/marketplace/opportunities/${id}`).then(r => r.data),
  bundles: () => client.get("/marketplace/bundles").then(r => r.data),
  facets: () => client.get("/marketplace/facets").then(r => r.data),
  recommendations: () => client.get("/marketplace/recommendations").then(r => r.data),
};

export const staffApi = {
  caseload: () => client.get("/staff/caseload").then(r => r.data),
  packets: () => client.get("/staff/handoff-packets").then(r => r.data),
  participant: (id) => client.get(`/staff/participants/${id}`).then(r => r.data),
  note: (d) => client.post("/staff/notes", d).then(r => r.data),
  referral: (d) => client.post("/staff/referrals", d).then(r => r.data),
};

export const refApi = {
  mine: () => client.get("/referrals/me").then(r => r.data),
  get: (id) => client.get(`/referrals/${id}`).then(r => r.data),
  outcome: (id, d) => client.post(`/referrals/${id}/outcome`, d).then(r => r.data),
  thread: (id) => client.get(`/referrals/${id}/messages`).then(r => r.data),
  post: (id, body) => client.post(`/referrals/${id}/messages`, { body }).then(r => r.data),
};

export const adminApi = {
  stats: () => client.get("/admin/stats").then(r => r.data),
  users: () => client.get("/admin/users").then(r => r.data),
  audit: () => client.get("/admin/audit").then(r => r.data),
  createUser: (d) => client.post("/admin/users", d).then(r => r.data),
};

/** COS App Center — canonical Application API. */
export const cosAppsApi = {
  list: () => client.get("/cos/apps").then(r => r.data),
  get: (id) => client.get(`/cos/apps/${id}`).then(r => r.data),
  manifest: (id) => client.get(`/cos/apps/${id}/manifest`).then(r => r.data),
  install: (id) => client.post(`/cos/apps/${id}/install`).then(r => r.data),
  uninstall: (id) => client.post(`/cos/apps/${id}/uninstall`).then(r => r.data),
  enable: (id) => client.post(`/cos/apps/${id}/enable`).then(r => r.data),
  disable: (id) => client.post(`/cos/apps/${id}/disable`).then(r => r.data),
  updateConfig: (id, config) =>
    client.patch(`/cos/apps/${id}/config`, { config }).then(r => r.data),
  license: (id) => client.get(`/cos/apps/${id}/license`).then(r => r.data),
};

/** COS Organization Identity Service — authoritative source of truth for
 *  organizational identity. Consumed by About page, footer, schema.org,
 *  email templates, invoice + proposal templates, and AI responses. */
export const platformIdentityApi = {
  /** Unauthenticated. Returns { primary, commercial_entity }. */
  public: () => client.get("/cos/platform/identity/public").then(r => r.data),
  /** Authenticated. Returns the caller's tenant identity or the primary. */
  mine: () => client.get("/cos/platform/identity").then(r => r.data),
  all: () => client.get("/cos/platform/identity/all").then(r => r.data),
  get: (iid) => client.get(`/cos/platform/identity/${iid}`).then(r => r.data),
  update: (iid, patch) =>
    client.patch(`/cos/platform/identity/${iid}`, patch).then(r => r.data),
};

export const accessApi = {
  request: (module, reason="") => client.post("/access/request", { module, reason }).then(r => r.data),
  resetPassword: (new_password) => client.post("/auth/reset-password", { new_password }).then(r => r.data),
};

export const bdApi = {
  enums: () => client.get("/bd/enums").then(r => r.data),
  dashboard: () => client.get("/bd/dashboard").then(r => r.data),
  listOrgs: (params) => client.get("/bd/organizations", { params }).then(r => r.data),
  getOrg: (id) => client.get(`/bd/organizations/${id}`).then(r => r.data),
  createOrg: (d) => client.post("/bd/organizations", d).then(r => r.data),
  patchOrg: (id, d) => client.patch(`/bd/organizations/${id}`, d).then(r => r.data),
  transitionStage: (id, stage, reason="") => client.post(`/bd/organizations/${id}/stage`, { stage, reason }).then(r => r.data),
  deleteOrg: (id) => client.delete(`/bd/organizations/${id}`).then(r => r.data),
  listContacts: (organization_id) => client.get("/bd/contacts", { params: { organization_id }}).then(r => r.data),
  createContact: (d) => client.post("/bd/contacts", d).then(r => r.data),
  patchContact: (id, d) => client.patch(`/bd/contacts/${id}`, d).then(r => r.data),
  listActivities: (params) => client.get("/bd/activities", { params }).then(r => r.data),
  createActivity: (d) => client.post("/bd/activities", d).then(r => r.data),
  patchActivity: (id, d) => client.patch(`/bd/activities/${id}`, d).then(r => r.data),
  listOpps: (params) => client.get("/bd/opportunities", { params }).then(r => r.data),
  createOpp: (d) => client.post("/bd/opportunities", d).then(r => r.data),
  listMous: (params) => client.get("/bd/mous", { params }).then(r => r.data),
  createMou: (d) => client.post("/bd/mous", d).then(r => r.data),
  listLeads: (params) => client.get("/bd/leads", { params }).then(r => r.data),
  convertLead: (id, d) => client.post(`/bd/leads/${id}/convert`, d).then(r => r.data),
  aiSummarize: (d) => client.post("/bd/ai/summarize", d).then(r => r.data),
  aiDraftEmail: (d) => client.post("/bd/ai/draft-email", d).then(r => r.data),
  // Institutional Development Hub — Session 19
  listPipelines: (params) => client.get("/bd/pipelines", { params }).then(r => r.data),
  createPipeline: (d) => client.post("/bd/pipelines", d).then(r => r.data),
  patchPipeline: (id, d) => client.patch(`/bd/pipelines/${id}`, d).then(r => r.data),
  deletePipeline: (id) => client.delete(`/bd/pipelines/${id}`).then(r => r.data),
  kanban: (params) => client.get("/bd/kanban", { params }).then(r => r.data),
  transitionOppStage: (id, stage, reason="") => client.post(`/bd/opportunities/${id}/stage`, { stage, reason }).then(r => r.data),
  analytics: () => client.get("/bd/analytics").then(r => r.data),
  // IDH Phase 2
  calendar: (params) => client.get("/bd/calendar", { params }).then(r => r.data),
  tasks: (scope="all") => client.get("/bd/tasks", { params: { scope }}).then(r => r.data),
  completeTask: (aid) => client.post(`/bd/tasks/${aid}/complete`).then(r => r.data),
  generateReminders: () => client.post("/bd/reminders/generate").then(r => r.data),
  exportUrl: (type) => `${API}/bd/export?type=${type}`,
};

export const employerApi = {
  dashboard: () => client.get("/employer/dashboard").then(r => r.data),
  getOrg: () => client.get("/employer/organization").then(r => r.data),
  patchOrg: (d) => client.patch("/employer/organization", d).then(r => r.data),
  listJobs: (status) => client.get("/employer/job-orders", { params: status ? { status } : {}}).then(r => r.data),
  getJob: (id) => client.get(`/employer/job-orders/${id}`).then(r => r.data),
  createJob: (d) => client.post("/employer/job-orders", d).then(r => r.data),
  patchJob: (id, d) => client.patch(`/employer/job-orders/${id}`, d).then(r => r.data),
  deleteJob: (id) => client.delete(`/employer/job-orders/${id}`).then(r => r.data),
  candidates: (params) => client.get("/employer/candidates", { params }).then(r => r.data),
  hiringActivity: () => client.get("/employer/hiring-activity").then(r => r.data),
};

export const onboardingApi = {
  employer: (d) => client.post("/onboarding/employer", d).then(r => r.data),
  partner: (d) => client.post("/onboarding/partner", d).then(r => r.data),
};

export const staffJobsApi = {
  list: (status = "open") => client.get("/staff/job-orders", { params: { status }}).then(r => r.data),
  markFilled: (jid, notes = "") => client.post(`/staff/job-orders/${jid}/mark-filled`, { notes }).then(r => r.data),
};

export const oicApi = {
  queues: () => client.get("/oic/queues/summary").then(r => r.data),
  list: (params) => client.get("/oic/opportunities", { params }).then(r => r.data),
  get: (id) => client.get(`/oic/opportunities/${id}`).then(r => r.data),
  action: (id, action, opts = {}) =>
    client.post(`/oic/opportunities/${id}/action`, { action, ...opts }).then(r => r.data),
  reasons: () => client.get("/oic/decisions/reasons").then(r => r.data),
  decisionsStats: () => client.get("/oic/decisions/stats").then(r => r.data),
  publish: (id, body) => client.post(`/oic/opportunities/${id}/publish`, body || {}).then(r => r.data),
  runAll: () => client.post("/oic/agents/run-all").then(r => r.data),
  runAgent: (agent) => client.post(`/oic/agents/${agent}/run`).then(r => r.data),
  agents: () => client.get("/oic/agents/status").then(r => r.data),
  rules: () => client.get("/oic/rules").then(r => r.data),
  updateRules: (rules) => client.put("/oic/rules", { rules }).then(r => r.data),
  thresholds: () => client.get("/oic/thresholds").then(r => r.data),
  updateThresholds: (t) => client.put("/oic/thresholds", t).then(r => r.data),
  sources: () => client.get("/oic/sources").then(r => r.data),
  createSource: (d) => client.post("/oic/sources", d).then(r => r.data),
  patchSource: (id, d) => client.patch(`/oic/sources/${id}`, d).then(r => r.data),
  deleteSource: (id) => client.delete(`/oic/sources/${id}`).then(r => r.data),
  analytics: () => client.get("/oic/analytics").then(r => r.data),
  execDashboard: () => client.get("/oic/exec-dashboard").then(r => r.data),
  institutionalMemory: (params) => client.get("/oic/institutional-memory", { params }).then(r => r.data),
  forecast: () => client.get("/oic/forecast").then(r => r.data),
  execBrief: () => client.get("/oic/exec-brief").then(r => r.data),
  generateExecBrief: () => client.post("/oic/exec-brief/generate").then(r => r.data),
};

export const publicOicApi = {
  list: (params) => client.get("/oic/public/opportunities", { params }).then(r => r.data),
  detail: (slug) => client.get(`/oic/public/opportunities/${slug}`).then(r => r.data),
};

export const adminInviteApi = {
  invite: (d) => client.post("/admin/users/invite", d).then(r => r.data),
  resend: (uid) => client.post(`/admin/users/${uid}/resend-invitation`).then(r => r.data),
  list: () => client.get("/admin/users/invitations").then(r => r.data),
};
